import notify from '../shared/feedback/notifications';
import FileSaver from 'file-saver';
import {
    DOWNLOAD_STARTED,
    DOWNLOAD_PROGRESS,
    DOWNLOAD_COMPLETED,
    DOWNLOAD_ERROR,
    DOWNLOAD_INIT_PROGRESS,
    DOWNLOAD_PROGRESS_NOT_SUPPORTED
} from './actionTypes';
import history from '../shared/history';
import { parseContentDispositionFilename } from '../shared/parseContentDispositionFilename';
import { Translate } from '../shared/LanguageService';
import { getUserManager } from '../shared/userManager';
import userStore from '../shared/UserStore';

let handle401Promise: Promise<any>;

export function downloadFile(url: string, data?: any) {
    return (dispatch: any) =>
        get(url, data, dispatch).then((response) => {
            return saveFile(response, dispatch);
        });
}

function get(url: string, q: any = {}, dispatch: any): Promise<any> {
    let queryString = Object.keys(q)
        .filter((x) => q[x])
        .map((k: any) => {
            if (q[k] instanceof Array) {
                let param = '';
                let array: any[] = q[k] as any[];
                for (let i = 0; i < array.length; i++) {
                    param += k + '=' + encodeURI(array[i]);
                    if (i < array.length - 1) {
                        param += '&';
                    }
                }
                return param;
            }
            return k + '=' + q[k];
        })
        .join('&');

    dispatch({ type: DOWNLOAD_STARTED });
    let request = () =>
        fetch(url + (queryString ? '?' + queryString : ''), {
            method: 'GET',
            headers: appendHeaders({}),
            mode: 'cors'
        });

    return executeRequest(request, dispatch);
}

function executeRequest(requestFunc: () => Promise<Response>, dispatch: any) {
    return handleRequest(requestFunc)
        .then((response) => {
            if (response.redirected) {
                window.location.href = response.url;
                dispatch({ type: DOWNLOAD_COMPLETED });
                return Promise.reject('redirect');
            } else {
                return response;
            }
        })
        .then(checkStatus)
        .then(parseResponse)
        .catch((error) => {
            dispatch({ type: DOWNLOAD_COMPLETED });
            if (error === 'redirect') {
                console.info('request chain redirected');
                return;
            }

            notify.failure(Translate('image-editor-firms/notification/error/unable-to-fetch-data'));

            // eslint-disable-next-line
            console.error('executeRequest error', error);
            throw error;
        });
}

function appendHeaders(headers: any = {}): any {
    headers['pragma'] = 'no-cache';
    headers['cache-control'] = 'no-cache';
    headers['access-control-allow-origin'] = '*';

    return addAuthorizationHeader(headers);
}

function addAuthorizationHeader(headers: any = {}): any {
    let authHeader = calculateAuthHeader();
    if (authHeader) {
        authHeader.forEach((x) => (headers[x.name] = x.value));
    }
    return headers;
}

function calculateAuthHeader() {
    let user = userStore.fetchUserFromLocalStorage();
    let impersonatedCountry = userStore.fetchCountryImpersonationFromSessionStorage();
    let accessToken = user && user.access_token ? user.access_token : undefined;
    let headers = [];
    if (accessToken) {
        headers.push({ name: 'Authorization', value: `Bearer ${accessToken}` });
    }
    if (user && user.impersonatedUser) {
        headers.push({ name: 'Impersonate', value: user.impersonatedUser });
    } else if (impersonatedCountry) {
        headers.push({
            name: 'ImpersonateCountry',
            value: impersonatedCountry.id
        });
    }
    return headers;
}

function checkStatus(response: Response) {
    if (response.status >= 200 && response.status < 300) {
        return response;
    } else {
        let errorMessage: string = response.statusText;
        let error = new Error(errorMessage) as any;
        error.response = response;
        throw error;
    }
}

function parseResponse(response: Response): any {
    const contentType = response.headers.get('content-type');

    if (contentType && contentType.indexOf('application/json') !== -1) {
        return response.json();
    } else if (contentType && contentType.indexOf('application/zip') !== -1) {
        return response;
    } else if (contentType && contentType.indexOf('image/jpeg') !== -1) {
        return response;
    } else if (contentType && contentType.indexOf('image/png') !== -1) {
        return response;
    } else if (contentType && contentType.indexOf('application/pdf') !== -1) {
        return response;
    } else if (
        contentType &&
        contentType.indexOf('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') !==
            -1
    ) {
        return response;
    } else if (contentType && contentType.indexOf('application/octet') !== -1) {
        return response;
    }
}

function handleResponse(requestFunc: () => Promise<Response>) {
    let userManager = getUserManager();

    return requestFunc().then((response) => {
        if (response.status === 401) {
            if (!handle401Promise) {
                handle401Promise = userManager
                    .signinSilent()
                    .then(() => {
                        handle401Promise = null;
                    })
                    .catch(() => {
                        userStore.removeUserFromStorage();
                        history.push('/');
                        notify.failure(Translate('api/authentication/error/session-timed-out'));
                        handle401Promise = null;
                    });
            }
            return handle401Promise.then(() => requestFunc());
        } else if (response.status === 404) {
            history.replace('/404');
            return response;
        } else if (response.status === 403) {
            history.replace('/no-access');
            return response;
        } else {
            return response;
        }
    });
}

function handleRequest(fetchRequest: () => Promise<Response>): Promise<Response> {
    let limit = 4;
    return new Promise((resolve, reject) => {
        let retryFetch = (numberOfTries) => {
            handleResponse(fetchRequest)
                .then((response) => {
                    resolve(response);
                })
                .catch((error) => {
                    if (numberOfTries > 0) {
                        retry(numberOfTries);
                    } else {
                        reject(error);
                    }
                });
        };

        let retry = (numberOfTries) => {
            setTimeout(() => retryFetch(--numberOfTries), 10000);
        };

        retryFetch(limit);
    });
}

function saveFile(response: any, dispatch: any): Promise<boolean> {
    if (!response) {
        return new Promise<boolean>((resolve) => {
            resolve(false);
        });
    }
    const contentLength = response.headers.get('content-length');
    if (!contentLength || !response.body) {
        dispatch({ type: DOWNLOAD_PROGRESS_NOT_SUPPORTED });

        return response.blob().then((file) => {
            let blob = new Blob([file], {
                type: response.headers.get('content-type')
            });
            FileSaver.saveAs(
                blob,
                parseContentDispositionFilename(response.headers.get('content-disposition'))
            );
            dispatch({ type: DOWNLOAD_COMPLETED });
        });
    } else {
        const total = parseInt(contentLength, 10);
        let loaded = 0;

        dispatch({ type: DOWNLOAD_INIT_PROGRESS, download: { total: total } });

        var newResponse = new Response(
            new ReadableStream({
                start(controller) {
                    const reader = response.body.getReader();

                    read();

                    function read() {
                        reader
                            .read()
                            .then(({ done, value }) => {
                                if (done) {
                                    controller.close();
                                    return;
                                }
                                loaded += value.byteLength;
                                dispatch({
                                    type: DOWNLOAD_PROGRESS,
                                    download: { loaded: loaded }
                                });

                                controller.enqueue(value);
                                read();
                            })
                            .catch((error) => {
                                controller.error(error);
                                dispatch({ type: DOWNLOAD_ERROR });
                            });
                    }
                }
            })
        );

        return newResponse.blob().then((file) => {
            let blob = new Blob([file], {
                type: response.headers.get('content-type')
            });
            FileSaver.saveAs(
                blob,
                parseContentDispositionFilename(response.headers.get('content-disposition'))
            );
            dispatch({ type: DOWNLOAD_COMPLETED });
            return true;
        });
    }
}
